import { z } from "zod";

import { enumReadSchema } from "../../../../shared/core/enum-read.schema";
import { VibeGitProjectIdPathVarsSchema } from "./git-projects.schemas";

/**
 * A ref as Forgejo's tree/contents API accepts it: a branch name or a
 * full-length commit sha. Deliberately NOT `VibeRefNameSchema` — that schema
 * requires the fully-qualified `refs/heads/…` form our own DB-mirrored
 * `VibeRef` rows use, and Forgejo's API takes the short form instead
 * (`main`, not `refs/heads/main`). Forgejo does its own resolution and 404s
 * on anything it cannot resolve, so this is a light sanity bound, not a
 * git-check-ref-format replica.
 */
export const VibeGitContentsRefSchema = z
  .string()
  .trim()
  .min(1, "ref is required")
  .max(255, "ref must be ≤ 255 characters")
  .refine((s) => !/\s/.test(s), { message: "ref must not contain whitespace" })
  .refine((s) => !s.includes(".."), { message: "ref must not contain '..'" });

/**
 * A repo-relative file or directory path. Trimmed of a leading slash and any
 * `..` component — defense in depth, even though this is relayed to the
 * tenant's own Forgejo rather than used for local filesystem access.
 */
export const VibeGitContentsPathSchema = z
  .string()
  .trim()
  .min(1, "path is required")
  .max(1024, "path must be ≤ 1024 characters")
  .refine((s) => !s.startsWith("/"), { message: "path must not start with '/'" })
  .refine((s) => !s.split("/").includes(".."), { message: "path must not contain '..' segments" });

// ----------------------------------------------------------------------------
// Tree — the file/directory listing for a ref, optionally scoped to a path
// ----------------------------------------------------------------------------

export const VibeGitTreeEntrySchema = z.object({
  path: z.string(),
  type: enumReadSchema(["file", "directory"] as const),
  sha: z.string(),
  /// `null` for a directory entry — Forgejo reports no size for tree rows.
  size: z.number().nullable()
});

export const GetVibeGitProjectTreePathVarsSchema = VibeGitProjectIdPathVarsSchema;

export const GetVibeGitProjectTreeQuerySchema = z.object({
  ref: VibeGitContentsRefSchema,
  /// Optional directory prefix. Filtered server-side, in-process — never
  /// sent to Forgejo, whose recursive-tree endpoint has no directory-scoped
  /// mode.
  path: VibeGitContentsPathSchema.optional()
});

export const GetVibeGitProjectTreeResponseSchema = z.object({
  entries: z.array(VibeGitTreeEntrySchema),
  /**
   * Forgejo's own flag off the same fetch — `true` means this is a PARTIAL
   * view of the ref (Forgejo caps the entry count on a large repo) and there
   * is more the caller has not seen. A path filter narrows `entries` but
   * never clears this: the entries held back could just as easily have been
   * under that same prefix. Surface it (a banner, a "showing partial
   * results" state) rather than rendering a partial tree as complete.
   */
  truncated: z.boolean()
});

// ----------------------------------------------------------------------------
// Blob — one file's content at a ref
// ----------------------------------------------------------------------------

export const VibeGitBlobContentSchema = z.object({
  path: z.string(),
  sha: z.string(),
  size: z.number(),
  encoding: z.literal("base64"),
  content: z.string()
});

export const GetVibeGitProjectBlobPathVarsSchema = VibeGitProjectIdPathVarsSchema;

export const GetVibeGitProjectBlobQuerySchema = z.object({
  ref: VibeGitContentsRefSchema,
  path: VibeGitContentsPathSchema
});

export const GetVibeGitProjectBlobResponseSchema = z.object({
  blob: VibeGitBlobContentSchema
});

// ----------------------------------------------------------------------------
// Branches — the DB-mirrored refs/heads/*, plain names
// ----------------------------------------------------------------------------

export const VibeGitProjectBranchSchema = z.object({
  name: z.string(),
  sha: z.string()
});

export const ListVibeGitProjectBranchesPathVarsSchema = VibeGitProjectIdPathVarsSchema;

export const ListVibeGitProjectBranchesResponseSchema = z.object({
  branches: z.array(VibeGitProjectBranchSchema)
});
